import { InferType, number, object, string, boolean } from 'yup';

export const kurvAccountProcessingSchema = object({
  ein: string()
    .transform((value) => (value ? value.trim() : null))
    .nullable()
    .matches(/^[0-9]*$/, 'Must be only digits')
    .length(9, 'Length must be 9 digits!')
    .required('EIN is a required field'),
  ssn: string()
    .transform((value) => (value ? value.trim() : null))
    .nullable()
    .matches(/^[0-9]*$/, 'Must be only digits')
    .length(9, 'Length must be 9 digits!')
    .required('SSN is a required field'),
  idNum: string()
    .transform((value) => (value ? value.trim() : null))
    .nullable()
    .matches(/^[0-9]*$/, 'Must be only digits')
    .max(20, 'Max length 20 digits!')
    .required('License is a required field'),
  dda: string()
    .transform((value) => (value ? value.trim() : null))
    .nullable()
    .matches(/^[0-9]*$/, 'Must be only digits')
    .max(17, 'Max length 17 digits!')
    .required('Direct Deposit Account is a required field'),
  sales_vol: string()
    .transform((value) => (value ? value.trim() : null))
    .nullable()
    .matches(/^[0-9]*$/, 'Must be only digits')
    .required('Maximum Monthly Sales Volume is a required field'),
});

export type IKurvAccountProcessingFormValues = InferType<typeof kurvAccountProcessingSchema>;

export const kurvBasicInformationSchema = object({
  processing: boolean().required(),
  ownership: number()
    .typeError('Must be a number')
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .min(0, 'Ownership must be between 0 and 100')
    .max(100, 'Ownership must be between 0 and 100')
    .required('Ownership is a required field'),
  address: string().max(32, 'Max length 32 chars!').required('Address is a required field'),
  city: string().max(32, 'Max length 32 chars!').required('City is a required field'),
  state: string().required('State is a required field'),
  county: string().max(128, 'Max length 128 chars!').required('County is a required field'),
  zip: string()
    .transform((value) => (value ? value.trim() : null))
    .nullable()
    .matches(/^[0-9]*$/, 'Must be only digits')
    .length(5, 'Length must be 5 digits!')
    .required('Zip is a required field'),
});

export type IKurvBasicInformationFormValues = InferType<typeof kurvBasicInformationSchema>;

export const kurvBusinessComplianceSchema = object({
  ccTermination: boolean().required(),
  ccThirdParty: boolean().required(),
  ccPciCompliant: boolean().required(),
  ccInvestigations: boolean().required(),
  ccVisaRiskPrograms: boolean().required(),
});

export type IKurvBusinessComplianceFormValues = InferType<typeof kurvBusinessComplianceSchema>;

export const kurvEquipmentSchema = object({
  retailKurvMobileB200: boolean(),
  retailKurvMobileB250: boolean(),
  retailDejavooZ8: boolean(),
  retailDejavooZ9: boolean(),
  retailIHaveMyOwn: boolean(),

  eComConvenuePay: boolean(),
  eComAuthNet: boolean(),
  eComIHaveMyOwn: boolean(),
});

export type IKurvEquipmentFormValues = InferType<typeof kurvEquipmentSchema>;

export const kurvSalesAndTransactionsSchema = object({
  swiped: string()
    .transform((value) => (value ? value.trim() : null))
    .nullable()
    .matches(/^[0-9]*$/, 'Must be only digits')
    .required('This field is required'),
  keyed: string()
    .transform((value) => (value ? value.trim() : null))
    .nullable()
    .matches(/^[0-9]*$/, 'Must be only digits')
    .required('This field is required')
    .test('max', 'Total must be 100%', function (value) {
      const { swiped } = this.parent;
      return Number(value) + Number(swiped) === 100;
    }),

  onSite: string()
    .transform((value) => (value ? value.trim() : null))
    .nullable()
    .matches(/^[0-9]*$/, 'Must be only digits')
    .required('This field is required'),
  offSite: string()
    .transform((value) => (value ? value.trim() : null))
    .nullable()
    .matches(/^[0-9]*$/, 'Must be only digits')
    .required('This field is required'),
  mailOrder: string()
    .transform((value) => (value ? value.trim() : null))
    .nullable()
    .matches(/^[0-9]*$/, 'Must be only digits')
    .required('This field is required'),
  telephoneOrder: string()
    .transform((value) => (value ? value.trim() : null))
    .nullable()
    .matches(/^[0-9]*$/, 'Must be only digits')
    .required('This field is required'),
  internet: string()
    .transform((value) => (value ? value.trim() : null))
    .nullable()
    .matches(/^[0-9]*$/, 'Must be only digits')
    .test('max', 'Total must be 100%', function (value) {
      const { onSite, offSite, mailOrder, telephoneOrder } = this.parent;
      const sum =
        Number(onSite) +
        Number(offSite) +
        Number(mailOrder) +
        Number(telephoneOrder) +
        Number(value);
      return sum === 100;
    })
    .required('This field is required'),
});

export type IKurvSalesAndTransactionsFormValues = InferType<typeof kurvSalesAndTransactionsSchema>;
